import {FormControl, FormGroup} from '@angular/forms';

import {BaseControl, SelectControl} from 'appShared/classes/controls';

import {
    AddNewPlatformFrontendControls,
    AddNewPlatformFrontendCurrencySelector,
    AddNewPlatformFrontendDescriptionSelector,
    AddNewPlatformFrontendNameSelector,
    AddNewPlatformFrontendPlatformIdSelector,
    AddNewPlatformFrontendPriceSelector
} from 'appAssets/descriptors';

import {
    HttpStatusCodesEnum,
    PlatformFrontendInputModel,
    PlatformFrontendResponseInterface,
    PlatormFrontendModel,
    ServerResponse,
    SesService
} from '@spa/api';
import {switchMap} from 'rxjs/internal/operators';
import {Helper} from 'appShared/classes';
import {PlatformFrontendFormErrorStatesEnum} from 'appEntries/panel/enums';
import {TradingPlatformInfoInterface} from '@spa/api/lib/interfaces';
import {BaseMarketDataForm} from 'appShared/forms';
import { CurrencyTrait } from 'appEntries/panel/traits';
import { CurrencyService } from 'appShared/services';
import { InArrayValidator } from '@spa/validators';

export class AddPlatformFrontendForm extends BaseMarketDataForm {
    private model: PlatormFrontendModel;
    public tradingPlatforms: TradingPlatformInfoInterface[];
    private currencyService: CurrencyService;

    constructor(sesService: SesService, currencyService: CurrencyService, tradingPlatforms: TradingPlatformInfoInterface[], model: PlatormFrontendModel = null) {
        super(sesService);
        this.model = model;
        this.tradingPlatforms = tradingPlatforms;
        this.currencyService = currencyService;
    }

    private createPlatform() {
        const name = this.formGroup.value[AddNewPlatformFrontendNameSelector];
        const description = this.formGroup.value[AddNewPlatformFrontendDescriptionSelector];
        const platformId = this.formGroup.value[AddNewPlatformFrontendPlatformIdSelector];
        var unformattedPrice = String(this.formGroup.value[AddNewPlatformFrontendPriceSelector]);
        const price = Helper.getNonFormattedAmount(unformattedPrice);

        const currency = this.formGroup.value[AddNewPlatformFrontendCurrencySelector];
        const inputModel = new PlatformFrontendInputModel(null, platformId, description, name, +price, currency);

        return this.sesService.createPlatformFrontend(inputModel)
        .pipe(switchMap(registerResponse => {
            if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                return Helper.errorsHelper().createErrorResponseObservable<PlatformFrontendResponseInterface>(
                    registerResponse,
                    PlatformFrontendFormErrorStatesEnum.CreatePlatformFrontendFailed
                );
            }

            return Helper.createSuccessResponseObservable<boolean>(true);

        }));
    }

    private updatePlatform() {
        const name = this.formGroup.value[AddNewPlatformFrontendNameSelector];
        const description = this.formGroup.value[AddNewPlatformFrontendDescriptionSelector];
        const platformId = this.formGroup.value[AddNewPlatformFrontendPlatformIdSelector];
        var unformattedPrice = String(this.formGroup.value[AddNewPlatformFrontendPriceSelector]);
        const price = Helper.getNonFormattedAmount(unformattedPrice);

        const currency = this.formGroup.value[AddNewPlatformFrontendCurrencySelector];
        const inputModel = new PlatformFrontendInputModel(this.model.id, platformId, description, name, +price, currency);

        return this.sesService.updatePlatformFrontend(this.model.id, inputModel)
        .pipe(switchMap(registerResponse => {
            if (registerResponse.status !== HttpStatusCodesEnum.Ok) {
                return Helper.errorsHelper().createErrorResponseObservable<PlatformFrontendResponseInterface>(
                    registerResponse,
                    PlatformFrontendFormErrorStatesEnum.UpdatePlatformFrontendFailed
                );
            }

            return Helper.createSuccessResponseObservable<boolean>(true);

        }));
    }

    private prepareTradingPlatformData(): void {
        const control = this.controls[2] as SelectControl;

        control.options = this.tradingPlatforms.map((item: TradingPlatformInfoInterface) => {
            return {
                label: item.name,
                value: item.id
            };
        });

        control.value = this.model?.platformId;
    }

    private prepareCurrencyData(): void {
        const currencies = []; 
        const control = this.controls[4] as SelectControl;
        control.options = CurrencyTrait.getSortedCurrencyAsOptions(this.currencyService.getAllCurrencies());
        currencies.push(...this.currencyService.getAllCurrencies().map(x => x.currencyCode)); 
        control.extendValidators(InArrayValidator(currencies));  
        control.disabled = control.options.length === 1;
    }

    initForm(): void {

        this.formGroup = new FormGroup({});

        if (!this.model) {

            this.controls = AddNewPlatformFrontendControls().map((control: BaseControl) => {
                this.formGroup.addControl(control.name, new FormControl(control.value, control.validators));

                return control;
            });

            this.prepareTradingPlatformData();
            this.prepareCurrencyData();

            return;
        }

        this.controls = AddNewPlatformFrontendControls().map((control: BaseControl) => {
            switch (control.name) {
                case AddNewPlatformFrontendNameSelector:
                    this.formGroup.addControl(control.name, new FormControl(this.model.name, control.validators));
                    break;
                case AddNewPlatformFrontendDescriptionSelector:
                    this.formGroup.addControl(control.name, new FormControl(this.model.description, control.validators));
                    break;
                case AddNewPlatformFrontendPlatformIdSelector:
                    this.formGroup.addControl(control.name, new FormControl(this.model.platformId, control.validators));
                    break;
                case AddNewPlatformFrontendPriceSelector:
                    this.formGroup.addControl(control.name, new FormControl(this.model.price, control.validators));
                    break;
                case AddNewPlatformFrontendCurrencySelector:
                    this.formGroup.addControl(control.name, new FormControl(this.model.currency, control.validators));
                    break;
            }

            return control;
        });

        this.prepareTradingPlatformData();
        this.prepareCurrencyData();
    }

    createRequest(): ServerResponse<boolean> {
        if (!this.formGroup.dirty) {
            return null;
        }

        if (!this.model) {
            return this.createPlatform();
        }

        return this.updatePlatform();
    }
}
